import React, { useState, useEffect } from 'react';
import {
  User, Palette, Globe, Link2, Wand2, Bell, Shield, CreditCard, X, Loader2, UserCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { supabase } from '../lib/supabase';
import { useTheme } from './theme-provider';
import { getPrefs, setPrefs, applyFontSize } from '../utils/prefs';
import ModelSelect from './ModelSelect';
import DriveConnectionPanel from './DriveConnectionPanel';
import BillingPanel from './BillingPanel';
import { AccountSummary, AppConnections, PersonalAccessTokens } from './UserProfile';

/**
 * The Settings dialog: left section nav, right content, Cancel / Save.
 *
 * Honesty rule for every control in here: a setting either does something
 * today (theme, font size, default model, password, connections, billing)
 * or its section says plainly what it affects. No decorative switches.
 */

const SECTIONS = [
  // r113b: ONE identity section. r113 added Account beside Profile and left
  // the standalone Account PAGE in place, which made three surfaces for the
  // same thing instead of one. Profile held the editable fields (name, role,
  // company); Account held the read-only facts (plan, member since, sign-in
  // method) plus connections and tokens. They are the same subject, so they
  // are now the same section.
  { id: 'account', label: 'Account', icon: User },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'language', label: 'Language & Region', icon: Globe },
  { id: 'connections', label: 'Connections', icon: Link2 },
  { id: 'ai', label: 'AI & Generation', icon: Wand2 },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'billing', label: 'Billing', icon: CreditCard },
];

const Row = ({ title, sub, children }) => (
  <div className="flex items-start justify-between gap-6 py-4 border-b border-border last:border-0">
    <div className="min-w-0">
      <p className="text-sm font-medium text-foreground">{title}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5.5 w-10 h-[22px] items-center rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-muted-foreground/25'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
  </button>
);

const Chips = ({ options, value, onChange }) => (
  <div className="flex gap-1.5">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
          value === o.value
            ? 'border-primary text-primary bg-primary/5 font-medium'
            : 'border-border text-muted-foreground hover:text-foreground'}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export default function SettingsModal({ open, onClose, user: userProp, usageData = null,
                                        initialSection = 'account' }) {
  const [section, setSection] = useState(initialSection);
  // r113c: land on the section the caller asked for. "Upgrade plan" that
  // opens on Account is a dead end — the user has to find Billing before
  // they can do the thing they just clicked. Re-applied on every open so a
  // second click from a different entry point does not inherit the last
  // section the user happened to leave it on.
  useEffect(() => {
    if (open) setSection(initialSection);
  }, [open, initialSection]);
  const { theme, toggleTheme } = useTheme();
  const [prefs, setPrefsState] = useState(getPrefs);
  const [saving, setSaving] = useState(false);
  // Self-sufficient: the sidebar only carries userId/userName strings, so
  // when no full user object is passed, load it from the session.
  const [loadedUser, setLoadedUser] = useState(null);
  const user = userProp || loadedUser;

  useEffect(() => {
    // Refetch on EVERY open: the modal stays mounted while closed, so a
    // cached user goes stale after a Save — the reopen would then seed the
    // fields from old metadata and the next Save would silently revert
    // the earlier change.
    if (!open || userProp) return;
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) setLoadedUser(data.user);
    }).catch(() => {});
  }, [open, userProp]);

  // profile draft
  const meta = user?.user_metadata || {};
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  // security draft
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');

  useEffect(() => {
    const dn = (meta.display_name || user?.email?.split('@')[0] || '').trim();
    const [f, ...rest] = dn.split(' ');
    setFirstName(f || '');
    setLastName(rest.join(' '));
    setRole(meta.role || '');
    setCompany(meta.company || '');
    if (open) {
      setPrefsState(getPrefs());
      setPw1(''); setPw2('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      // when a dropdown (ModelSelect) is open, Escape belongs to it —
      // both listen on document, so without this guard one keypress
      // closed the dropdown AND the whole modal, discarding edits
      if (e.key === 'Escape' && !document.querySelector('[data-wiz-dropdown-open]')) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const patchPrefs = (patch) => setPrefsState((p) => ({ ...p, ...patch }));
  const patchNotif = (k, v) => setPrefsState((p) => ({
    ...p, notifications: { ...p.notifications, [k]: v },
  }));

  const save = async () => {
    setSaving(true);
    try {
      // prefs: instant-effect ones were already applied; persist the set
      setPrefs(prefs);
      applyFontSize(prefs.fontSize);
      // profile metadata
      const display_name = `${firstName.trim()} ${lastName.trim()}`.trim();
      if (user) {
        const { data: upd, error } = await supabase.auth.updateUser({
          data: { display_name, role: role.trim(), company: company.trim() },
        });
        if (error) throw error;
        // adopt the fresh user so a reopen seeds from what was just saved
        if (upd?.user) setLoadedUser(upd.user);
      } else if (display_name || role.trim() || company.trim()) {
        throw new Error('Profile is still loading — try saving again in a moment');
      }
      // password, only when the user typed one
      if (pw1 || pw2) {
        if (pw1 !== pw2) throw new Error('Passwords do not match');
        if (pw1.length < 8) throw new Error('Password must be at least 8 characters');
        const { error } = await supabase.auth.updateUser({ password: pw1 });
        if (error) throw error;
        setPw1(''); setPw2('');
        toast.success('Password updated');
      }
      toast.success('Settings saved');
      onClose();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex w-full max-w-6xl h-[680px] max-h-[90vh] rounded-2xl bg-card border border-border shadow-2xl overflow-hidden">

        {/* ── left nav ── */}
        <div className="w-52 shrink-0 border-r border-border bg-muted/30 flex flex-col">
          <div className="px-5 pt-5 pb-3">
            <h2 className="text-base font-semibold text-foreground">Settings</h2>
          </div>
          <nav className="flex-1 px-2.5 space-y-0.5 overflow-y-auto">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                  section === s.id
                    ? 'bg-card text-foreground font-medium shadow-sm border border-border'
                    : 'text-muted-foreground hover:text-foreground'}`}
              >
                <s.icon className="w-3.5 h-3.5" />
                {s.label}
              </button>
            ))}
          </nav>
          <p className="px-5 py-3 text-[10px] text-muted-foreground/60 font-mono">
            wizardly.ai
          </p>
        </div>

        {/* ── right content ── */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">
              {SECTIONS.find((s) => s.id === section)?.label}
            </h3>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-2">
            {section === 'appearance' && (
              <>
                <Row title="Dark mode" sub="Switch the interface to the dark theme">
                  <Toggle checked={theme === 'dark'} onChange={() => toggleTheme()} />
                </Row>
                <Row title="Font size" sub="Adjust the base text size">
                  <Chips
                    options={[{ value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium' }, { value: 'large', label: 'Large' }]}
                    value={prefs.fontSize}
                    onChange={(v) => { patchPrefs({ fontSize: v }); applyFontSize(v); }}
                  />
                </Row>
              </>
            )}

            {section === 'language' && (
              <>
                <Row title="Interface language" sub="The language used across the app">
                  <select
                    value={prefs.language}
                    onChange={(e) => patchPrefs({ language: e.target.value })}
                    className="px-3 py-1.5 rounded-lg border border-border bg-card text-sm text-foreground"
                  >
                    <option value="en">English</option>
                    <option value="ar">العربية (coming soon)</option>
                  </select>
                </Row>
                <Row title="Timezone" sub="Used for timestamps and scheduling">
                  <select
                    value={prefs.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                    onChange={(e) => patchPrefs({ timezone: e.target.value })}
                    className="px-3 py-1.5 rounded-lg border border-border bg-card text-sm text-foreground max-w-[240px]"
                  >
                    {[Intl.DateTimeFormat().resolvedOptions().timeZone, 'Asia/Dubai', 'Asia/Riyadh', 'Asia/Amman', 'Asia/Qatar', 'Europe/London', 'UTC']
                      .filter((v, i, a) => a.indexOf(v) === i)
                      .map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </Row>
                <Row title="Date format">
                  <Chips
                    options={[{ value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' }, { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' }, { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' }]}
                    value={prefs.dateFormat}
                    onChange={(v) => patchPrefs({ dateFormat: v })}
                  />
                </Row>
              </>
            )}

            {section === 'connections' && (
              <div className="py-4">
                <p className="text-xs text-muted-foreground mb-4">
                  Connect external services to attach files, import data, and share presentations directly.
                </p>
                <DriveConnectionPanel userId={user?.id} variant="page" />
                <p className="text-[11px] text-muted-foreground mt-4">
                  CRM/ERP connections (HubSpot, Odoo) and personal access tokens are under{' '}
                  <button type="button" onClick={() => setSection('account')}
                          className="font-medium text-foreground underline underline-offset-2">
                    Account
                  </button>.
                </p>
              </div>
            )}

            {section === 'ai' && (
              <>
                <Row title="Default AI model" sub="Used whenever you don't pick a model in a flow">
                  <ModelSelect
                    value={prefs.defaultModel}
                    onChange={(v) => patchPrefs({ defaultModel: v })}
                  />
                </Row>
                <Row title="Cost confirmation" sub="Estimate credits and tokens before each generation, and ask to proceed">
                  <Toggle checked={!!prefs.confirmCost} onChange={(v) => patchPrefs({ confirmCost: v })} />
                </Row>
                <p className="text-[11px] text-muted-foreground py-3">
                  “Decide for me” follows the product default (currently Kimi K3), so it improves
                  automatically as better models ship. Premium models are governed by your plan's
                  monthly allowances.
                </p>
              </>
            )}

            {section === 'notifications' && (
              <>
                <Row title="Email notifications" sub="Get updates about your presentations">
                  <Toggle checked={prefs.notifications.email} onChange={(v) => patchNotif('email', v)} />
                </Row>
                <Row title="In-app notifications" sub="Alerts inside the wizardly.ai interface">
                  <Toggle checked={prefs.notifications.inApp} onChange={(v) => patchNotif('inApp', v)} />
                </Row>
                <Row title="Collaboration mentions" sub="Notify when someone mentions you">
                  <Toggle checked={prefs.notifications.mentions} onChange={(v) => patchNotif('mentions', v)} />
                </Row>
                <Row title="Generation complete" sub="Alert when a presentation is ready">
                  <Toggle checked={prefs.notifications.generationComplete} onChange={(v) => patchNotif('generationComplete', v)} />
                </Row>
                <p className="text-[11px] text-muted-foreground py-3">
                  These preferences are saved with your account on this device. Email delivery
                  requires notifications to be enabled by your workspace.
                </p>
              </>
            )}

            {section === 'security' && (
              <>
                <Row title="New password" sub="Minimum 8 characters">
                  <input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)}
                    autoComplete="new-password"
                    className="w-60 px-3 py-1.5 rounded-lg border border-border bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                </Row>
                <Row title="Confirm password">
                  <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                    autoComplete="new-password"
                    className="w-60 px-3 py-1.5 rounded-lg border border-border bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                </Row>
                <Row title="Sign out everywhere" sub="Ends every active session, including this one">
                  <Button
                    size="sm" variant="outline"
                    onClick={async () => {
                      try {
                        await supabase.auth.signOut({ scope: 'global' });
                      } catch (e) {
                        toast.error(String(e.message || e));
                      }
                    }}
                  >
                    Sign out all sessions
                  </Button>
                </Row>
              </>
            )}

            {section === 'account' && (
              <div className="py-4 space-y-6">
                <AccountSummary user={user} userName={`${firstName} ${lastName}`.trim()}
                                usageData={usageData} />
                <div>
                  <Row title="Name">
                    <div className="flex gap-2">
                      <input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First"
                        className="w-28 px-3 py-1.5 rounded-lg border border-border bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                      <input value={lastName} onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last"
                        className="w-28 px-3 py-1.5 rounded-lg border border-border bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                    </div>
                  </Row>
                  <Row title="Role">
                    <input value={role} onChange={(e) => setRole(e.target.value)}
                      placeholder="e.g. Designer"
                      className="w-60 px-3 py-1.5 rounded-lg border border-border bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                  </Row>
                  <Row title="Company">
                    <input value={company} onChange={(e) => setCompany(e.target.value)}
                      placeholder="e.g. Prezlab"
                      className="w-60 px-3 py-1.5 rounded-lg border border-border bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                  </Row>
                </div>
                <AppConnections userId={user?.id} />
                <PersonalAccessTokens userId={user?.id} />
              </div>
            )}

            {section === 'billing' && (
              <div className="-mt-6">
                {/* BillingPanel brings its own card chrome and hides itself
                    entirely when billing is not configured. */}
                <BillingPanel userId={user?.id} email={user?.email} />
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border">
            <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
